import Analytics from "../Analytics";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import find from "lodash/find";
import {
  BRAND_NAME,
  CONTRACT_TYPES,
  COUNTRY,
  ESIM,
  forms,
  GTM_ID,
  NUMBER_CATEGORY,
  NUMBER_TYPES,
  PSIM,
  SELF_ACTIVATION_SIM,
  SIM,
  SIM_TYPES
} from "utils/constants";
import {
  ANALYTICS_TYPES,
  GTM_DATALAYER_EVENT_MAPPING,
  GTM_EVENTS_NAME
} from "../../constants/AnalyticsTypeConstants";
import TagManager from "react-gtm-module";
import {
  compactJoin,
  getChannelFromUrl,
  getURLParams,
  isMobile
} from "utils/helpers";
import Session from "session";
import { getItem, setItem } from "utils/localStorage";
import { currencyCode } from "analytics/index";
class GoogleTagManagerAnalytics extends Analytics {
  pushEvent(event, data, message, country = COUNTRY.toLowerCase()) {
    /**
     * Push events to Google Tag Manager
     * @param {string} event type of event triggered based on user actions, page load and etc.
     * @param {object} data data associated with triggered event
     */
    const currentUrl = get(window, "location.pathname", "");
    if (event === GTM_EVENTS_NAME.PAGE_VISIT) {
      this.pushPageVisit(data, event);
    } else {
      this.pushToDataLayer(event, data, currentUrl, country, GTM_ID);
    }
  }

  pushPageVisit(data, event) {
    if (GTM_ID) {
      const dataLayer = buildPageVisitEventDataLayer(data, event);
      TagManager.dataLayer({ dataLayer });
    }
  }

  pushToDataLayer(
    event,
    data,
    currentUrl,
    country,
    googleTagManagerId = get(window, "GOOGLE_TAG_MANAGER_ID"),
    tagManager = TagManager
  ) {
    /**
     * Pushes Analytics events to Google Tag Manager
     * @param {string} event type of event triggered based on user actions, page load and etc.
     * @param {object} data data associated with triggered event
     * @param {string} country current country
     * @param {string} googleTagManagerId account ID of Google Tag Manager, initialized through ConfigManager
     */
    const gtmDataLayerEvent = GTM_DATALAYER_EVENT_MAPPING[event];

    // check if event needs to trigger google tag manager
    if (gtmDataLayerEvent) {
      // check if current country uses google tag manager
      const countrySupported = find(gtmDataLayerEvent, country);

      if (countrySupported) {
        // check if google tag manager needs to trigger on currentUrl
        const urlSupported = find(gtmDataLayerEvent.valid_countries[country], [
          "trigger_urls",
          [currentUrl]
        ]);

        if (urlSupported) {
          // push to dataLayer only if google tag manager ID exists
          if (googleTagManagerId) {
            const dataLayerParams = constructDataLayerParams(
              data,
              urlSupported?.event
            );
            const {
              event: dataLayerParamEventName,
              ...dataLayerParamsRest
            } = dataLayerParams;
            const transformedDataLayerParams = {
              event: dataLayerParamEventName,
              ecommerce: dataLayerParamsRest
            };
            const gtmEvent =
              transformedDataLayerParams?.event || urlSupported?.event;
            const tagManagerArgs = {
              dataLayer: transformedDataLayerParams
            };

            this.logEventStart(
              ANALYTICS_TYPES.gtm,
              gtmEvent,
              "GoogleTagManager",
              transformedDataLayerParams
            );
            // push to Google Tag Manager
            tagManager.dataLayer(tagManagerArgs);
            console.log(
              `Pushed GoogleTagManagerEvent ${gtmEvent} through GTM_MODULE`,
              transformedDataLayerParams
            );
            this.logEventFinish(
              ANALYTICS_TYPES.gtm,
              gtmEvent,
              "GoogleTagManager",
              transformedDataLayerParams
            );
          }
        }
      }
    }
  }
}

export const constructDataLayerParams = (
  data,
  event = GTM_EVENTS_NAME.ECOMM_EVENT
) => {
  /**
   * Constructs data to be pushed into Google Tag Manager
   * @param {object} data Data associated with event
   * @return {object} Data to be pushed into Google Tag Manager
   */

  switch (event) {
    case GTM_EVENTS_NAME.ECOMM_EVENT:
      return buildEcommDataLayer(data, event);
    case GTM_EVENTS_NAME.CUSTOM_PURCHASE:
    case GTM_EVENTS_NAME.ORDER_PURCHASE:
    case GTM_EVENTS_NAME.PURCHASE:
    case GTM_EVENTS_NAME.ADDONS_SELECTED:
      return buildAddonsSelectedEventDataLayer(data, event);
    case GTM_EVENTS_NAME.PERSONAL_DETAIL_SUBMIT:
      return buildPersonalDetailFormSubmitEventDataLayer(data, event);
    case GTM_EVENTS_NAME.SIM_VERIFICATION:
    case GTM_EVENTS_NAME.SIM_VERIFICATION_CLICK:
      return buildSimVerificationClickEventDataLayer(data, event);
    case GTM_EVENTS_NAME.NUMBER_SELECTED_PAGE_VIEW:
      return buildNumberSelectedPageViewEventDataLayer(data, event);
    case GTM_EVENTS_NAME.CURRENT_TELCO:
      return buildCurrentTelcoEventDataLayer(data, event);
    case GTM_EVENTS_NAME.PLAN_TYPE:
      return buildPlanTypeEventDataLayer(data, event);
    case GTM_EVENTS_NAME.NRIC_STATUS:
      return buildNRICStatusEventDataLayer(data, event);
    case GTM_EVENTS_NAME.EXISTING_NUMBER_ENTERED:
      return buildExistingNumberEnteredEventDataLayer(data, event);
    case GTM_EVENTS_NAME.NEW_NUMBER_SELECTED:
      return buildNewNumberSelectedEventDataLayer(data, event);
    case GTM_EVENTS_NAME.SADPURCHASE:
      return buildSadPurchaseEventDataLayer(data, event);
    case GTM_EVENTS_NAME.ACTIVATE:
      return buildActivateEventDataLayer(data, event);
    case GTM_EVENTS_NAME.PROMO_CODE_APPLIED:
      return buildPromoCodeAppliedEventDataLayer(data, event);
    case GTM_EVENTS_NAME.NRIC_NUMBER_VERIFIED:
      return buildNRICNumberVerifiedEventDataLayer(data, event);
    case GTM_EVENTS_NAME.NUMBER_TYPE_SELECTED:
      return buildNumberTypeSelectedEventDataLayer(data, event);
    case GTM_EVENTS_NAME.MYINFO:
      return buildMYINFOEventDataLayer(data, event);
    case GTM_EVENTS_NAME.MYINFO_TROUBLE:
      return buildMYINFOTroubleEventDataLayer(data, event);
    case GTM_EVENTS_NAME.VERIFY_PSIM_NUMBER_CLICK:
      return buildVerifyPSimNumberClickEventDataLayer(data, event);
    case GTM_EVENTS_NAME.PSIM_NUMBER_VERIFIED:
      return buildPSimNumberVerifiedEventDataLayer(data, event);
    case GTM_EVENTS_NAME.VERIFICATION_OTP_ENTERED:
      return buildVerificationOTPEnteredEventDataLayer(data, event);
    default:
      break;
  }

  return {};
};

const constructCommonEventDataObj = data => {
  const flowChannel = getChannelFromUrl();
  const customerID = Session.getDeviceId();

  if (
    flowChannel === SELF_ACTIVATION_SIM &&
    [ESIM, PSIM, SIM].includes(window.location.pathname?.split("/")[2])
  ) {
    const type = window.location.pathname?.split("/")[2];
    if (type === SIM_TYPES.ESIM) {
      setItem("simType", ESIM);
    } else if (type === SIM_TYPES.PSIM) {
      setItem("simType", PSIM);
    } else {
      setItem("simType", PSIM);
    }
  }

  if (data?.formValues?.number_type) {
    setItem("numberType", data?.formValues?.number_type);
  }

  if (data?.isPortIn === true) {
    setItem("numberType", NUMBER_TYPES.PORT_IN);
  } else if (data?.isPortIn === false) {
    setItem("numberType", NUMBER_TYPES.NEW_NUMBER);
  }

  if (data?.referral_code_type) {
    setItem("referralCodeType", data?.referral_code_type);
  }

  if (data?.referral_code) {
    setItem("coupon", data.referral_code);
  }

  if (data?.form === "PortInForm" && data?.field === "telco") {
    setItem("portinDonor", data?.payload);
  }

  let existingPlanTypeSelected =
    data?.formValues?.port_in_existing_plan_type ?? "postpaid";

  const simType = getItem("simType");
  let orderChannel = null;
  if (simType === SIM_TYPES.ESIM) {
    orderChannel = ESIM;
  } else if (simType === SIM_TYPES.PSIM) {
    orderChannel = PSIM;
  }

  const selected_number = data?.selected_number;
  if (selected_number) {
    let numberCategory = null;
    if (selected_number.gold) {
      numberCategory = NUMBER_CATEGORY.GOLDEN;
    } else if (selected_number.silver) {
      numberCategory = NUMBER_CATEGORY.SILVER;
    } else {
      numberCategory = NUMBER_CATEGORY.FREE;
    }
    setItem("numberCategory", numberCategory);
  }

  return {
    transactionId: data?.order_ref,
    simType,
    flowChannel,
    orderChannel,
    customerID,
    existingPlanTypeSelected,
    numberType: getItem("numberType"),
    referralCodeType: getItem("referralCodeType"),
    portinDonor: getItem("portinDonor"),
    coupon: getItem("coupon"),
    numberCategory: getItem("numberCategory")
  };
};

const buildEcommDataLayer = (data, event) => ({
  event,
  transactionId: get(data, "order_ref", null), // going into dataLayer, therefore fallback on null value
  transactionProductType: get(data, "plan_name", null), // going into dataLayer, therefore fallback on null value
  transactionTotal: get(data, "orderParams.order_summary.onetime_total_fee", 0),
  transactionQuantity: 1
});

const buildPurchaseEventDataLayer = (data, event) => {
  const {
    channel,
    plan_name,
    order_ref,
    order,
    orderParams,
    currency,
    payment_status,
    is_multi_sim,
    family_plan_sim_count,
    planService,
    customer_id,
    contact_email,
    contact_number,
    order_deliver_detail
  } = data || {};
  const {
    channel_name,
    sim_count,
    selected_number,
    msisdn_source,
    portin_detail,
    phone_only,
    id_upload_pending,
    order_summary,
    user_detail,
    billing_detail,
    deliver_detail,
    order_user_detail_attributes,
    order_deliver_detail_attributes
  } = orderParams || {};

  const { onetime_total_fee, delivery_slot_fee } = order_summary || {};
  const orderRef = order_ref || order?.order_ref;
  const orderChannel = channel || channel_name;
  const orderSimCount = is_multi_sim ? 2 : sim_count || 1;
  const simCount = family_plan_sim_count
    ? family_plan_sim_count
    : orderSimCount;
  const shipping =
    delivery_slot_fee || data?.delivery_slot_fee || order?.delivery_slot_fee || 0;
  const isPortIn = msisdn_source === CONTRACT_TYPES.PORT_IN;
  const isESim = orderChannel === SIM_TYPES.ESIM;
  const customerID = customer_id || Session.getDeviceId();
  let user = !isEmpty(user_detail) ? user_detail : order_user_detail_attributes;
  user = !isEmpty(user)
    ? user
    : { email: contact_email, phone_number: contact_number };
  let address = !isEmpty(billing_detail) ? billing_detail : deliver_detail;
  address = !isEmpty(address) ? address : order_deliver_detail_attributes;
  address = !isEmpty(address) ? address : order_deliver_detail;
  const commonEventData = constructCommonEventDataObj(data);
  return {
    event,
    orderChannel,
    flowChannel: getChannelFromUrl(),
    idUploadPending: id_upload_pending,
    planName: plan_name,
    phoneOnly: phone_only,
    orderRef,
    customerID,
    purchaseID: orderRef,
    transactionId: orderRef,
    value: onetime_total_fee,
    orderSummary: order_summary,
    tax: planService?.tax_percentage || 0,
    shipping,
    currency,
    coupon: commonEventData.coupon,
    couponType: commonEventData.referralCodeType,
    simCount: phone_only ? 0 : simCount,
    simType: isESim ? SIM_TYPES.ESIM : SIM_TYPES.PHYSICAL_SIM,
    numberType: msisdn_source || commonEventData.numberType,
    portinDonor: isPortIn
      ? portin_detail?.port_donor
      : commonEventData.portinDonor,
    portinType: isPortIn
      ? portin_detail?.is_postpaid
        ? "postpaid"
        : "prepaid"
      : "",
    selectedNumber: isPortIn ? portin_detail?.port_number : selected_number,
    journeyType: "Non-SAD New",
    paymentStatus: payment_status,
    items: constructItems(data),
    ...constructEnhancedConversionData(user, address),
    numberCategory: commonEventData.numberCategory,
    existingPlanTypeSelected: commonEventData.existingPlanTypeSelected
  };
};

const buildPersonalDetailFormSubmitEventDataLayer = (data, event) => {
  const purchaseData = buildPurchaseEventDataLayer(data, event);
  const commonEventData = constructCommonEventDataObj(data);
  return {
    event,
    transactionId: commonEventData.transactionId,
    simType: commonEventData.simType,
    numberType: purchaseData.numberType,
    planName: purchaseData.planName,
    customerID: purchaseData.customerID,
    orderChannel: commonEventData.orderChannel,
    flowChannel: purchaseData.flowChannel,
    numberCategory: purchaseData.numberCategory,
    portinDonor: purchaseData.portinDonor,
    existingPlanTypeSelected: purchaseData.existingPlanTypeSelected,
    coupon: purchaseData.coupon,
    couponType: purchaseData.couponType
  };
};

const buildSadPurchaseEventDataLayer = (data, event) => {
  const purchaseData = buildPurchaseEventDataLayer(data, event);
  return {
    ...purchaseData,
    sadPurchaseID: purchaseData?.purchaseID,
    journeyType: "SAD",
    paymentStatus: "n/a"
  };
};

const buildActivateEventDataLayer = (data, event) => {
  const purchaseData = buildPurchaseEventDataLayer(data, event);
  return {
    event,
    transactionId: purchaseData.transactionId,
    simType: purchaseData.simType,
    numberType: purchaseData.numberType,
    planName: purchaseData.planName,
    customerID: purchaseData.customerID,
    orderChannel: purchaseData.orderChannel,
    flowChannel: purchaseData.flowChannel,
    numberCategory: purchaseData.numberCategory,
    portinDonor: purchaseData.portinDonor,
    existingPlanTypeSelected: purchaseData.existingPlanTypeSelected,
    coupon: purchaseData.coupon,
    couponType: purchaseData.couponType,
    enhancedConversionData: purchaseData.enhanced_conversion_data,
    value:purchaseData.value,
    currency: purchaseData?.currency,
    journeyType: purchaseData.journeyType,
    shipping: purchaseData.shipping || 0,
    paymentStatus: purchaseData.paymentStatus || "Success"
    
  };
};

const buildPromoCodeAppliedEventDataLayer = (data, event) => {
  const commonEventData = constructCommonEventDataObj(data);
  const { formValues } = data || {};
  const { number_type } = formValues || {};
  const customerID = Session.getDeviceId();
  let cartAmount = data?.planService?.pay_today?.[0]?.value ?? data?.orderParams?.order_summary?.onetime_total_fee ??  0;
  cartAmount =  parseFloat(cartAmount).toFixed(2);
  return {
    event,
    transactionId: commonEventData.transactionId,
    simType: commonEventData.simType,
    planName: data?.plan_name,
    orderChannel: commonEventData.orderChannel,
    portinDonor: commonEventData.portinDonor,
    existingPlanTypeSelected: commonEventData.existingPlanTypeSelected,
    flowChannel: getChannelFromUrl(),
    customerID,
    coupon: commonEventData.coupon,
    numberType: number_type || commonEventData.numberType,
    couponType: commonEventData.referralCodeType,
    value: cartAmount,
    currency: currencyCode()
  };
};

const buildPageVisitEventDataLayer = (_, event) => {
  return {
    event,
    urlParams: getURLParams(),
    env: isMobile() ? "mobile" : "desktop",
    host: window.location.host,
    path: window.location.pathname,
    sessionId: Session.getDeviceId(),
    ipAddress: Session.getIpAddress()
  };
};

const buildSimVerificationClickEventDataLayer = (data, event) => {
  const commonEventData = constructCommonEventDataObj(data);

  return {
    event,
    transactionId: commonEventData.transactionId,
    simType: commonEventData.simType,
    planName: data?.plan_name,
    customerID: commonEventData.customerID,
    orderChannel: commonEventData.orderChannel,
    flowChannel: commonEventData.flowChannel
  };
};

const buildNRICNumberVerifiedEventDataLayer = (data, event) => {
  const commonEventData = constructCommonEventDataObj(data);

  return {
    event,
    transactionId: commonEventData.transactionId,
    simType: commonEventData.simType,
    planName: data?.plan_name,
    customerID: commonEventData.customerID,
    orderChannel: commonEventData.orderChannel,
    flowChannel: commonEventData.flowChannel
  };
};

const buildNumberSelectedPageViewEventDataLayer = (data, event) => {
  const commonEventData = constructCommonEventDataObj(data);

  return {
    event,
    transactionId: commonEventData.transactionId,
    simType: commonEventData.simType,
    planName: data?.plan_name,
    customerID: commonEventData.customerID,
    orderChannel: commonEventData.orderChannel,
    flowChannel: commonEventData.flowChannel,
    numberType: data?.formValues?.number_type
  };
};

const buildNumberTypeSelectedEventDataLayer = (data, event) => {
  const commonEventData = constructCommonEventDataObj(data);

  return {
    event,
    transactionId: commonEventData.transactionId,
    simType: commonEventData.simType,
    planName: data?.plan_name,
    customerID: commonEventData.customerID,
    orderChannel: commonEventData.orderChannel,
    flowChannel: commonEventData.flowChannel,
    numberType:
      data?.isPortIn === true ? NUMBER_TYPES.PORT_IN : NUMBER_TYPES.NEW_NUMBER
  };
};

const buildNewNumberSelectedEventDataLayer = (data, event) => {
  const commonEventData = constructCommonEventDataObj(data);

  return {
    event,
    transactionId: commonEventData.transactionId,
    simType: commonEventData.simType,
    planName: data?.plan_name,
    customerID: commonEventData.customerID,
    orderChannel: commonEventData.orderChannel,
    flowChannel: commonEventData.flowChannel,
    numberType: data?.formValues?.number_type || commonEventData.numberType,
    numberCategory: commonEventData.numberCategory
  };
};

const buildExistingNumberEnteredEventDataLayer = (data, event) => {
  const commonEventData = constructCommonEventDataObj(data);

  return {
    event,
    transactionId: commonEventData.transactionId,
    simType: commonEventData.simType,
    planName: data?.plan_name,
    customerID: commonEventData.customerID,
    orderChannel: commonEventData.orderChannel,
    flowChannel: commonEventData.flowChannel,
    numberType: NUMBER_TYPES.PORT_IN
  };
};

const buildCurrentTelcoEventDataLayer = (data, event) => {
  const commonEventData = constructCommonEventDataObj(data);

  return {
    event,
    transactionId: commonEventData.transactionId,
    simType: commonEventData.simType,
    planName: data?.plan_name,
    customerID: commonEventData.customerID,
    orderChannel: commonEventData.orderChannel,
    flowChannel: commonEventData.flowChannel,
    numberType:
      data?.form === forms.PORT_IN_FORM
        ? NUMBER_TYPES.PORT_IN
        : NUMBER_TYPES.NEW_NUMBER,
    portinDonor: commonEventData.portinDonor
  };
};

const buildPlanTypeEventDataLayer = (data, event) => {
  const commonEventData = constructCommonEventDataObj(data);

  return {
    event,
    transactionId: commonEventData.transactionId,
    simType: commonEventData.simType,
    planName: data?.plan_name,
    customerID: commonEventData.customerID,
    orderChannel: commonEventData.orderChannel,
    flowChannel: commonEventData.flowChannel,
    numberType: NUMBER_TYPES.PORT_IN,
    existingPlanTypeSelected: commonEventData.existingPlanTypeSelected,
    portinDonor: commonEventData.portinDonor
  };
};

const buildNRICStatusEventDataLayer = (data, event) => {
  const commonEventData = constructCommonEventDataObj(data);

  return {
    event,
    transactionId: commonEventData.transactionId,
    simType: commonEventData.simType,
    planName: data?.plan_name,
    customerID: commonEventData.customerID,
    orderChannel: commonEventData.orderChannel,
    flowChannel: commonEventData.flowChannel,
    numberType: NUMBER_TYPES.PORT_IN,
    existingPlanTypeSelected: commonEventData.existingPlanTypeSelected,
    portinDonor: commonEventData.portinDonor
  };
};

const buildAddonsSelectedEventDataLayer = (data, event) => {
  const commonEventData = constructCommonEventDataObj(data);

  return {
    event,
    transactionId: commonEventData.transactionId,
    simType: commonEventData.simType,
    planName: data?.plan_name,
    customerID: commonEventData.customerID,
    orderChannel: commonEventData.orderChannel,
    flowChannel: commonEventData.flowChannel,
    numberType: data?.formValues?.number_type || commonEventData.numberType,
    existingPlanTypeSelected: commonEventData.existingPlanTypeSelected,
    portinDonor: commonEventData.portinDonor,
    numberCategory: commonEventData.numberCategory,
    coupon: commonEventData.coupon,
    couponType: commonEventData.referralCodeType
  };
};

const buildMYINFOEventDataLayer = (data, event) => {
  const commonEventData = constructCommonEventDataObj(data);

  return {
    event,
    transactionId: commonEventData.transactionId,
    simType: commonEventData.simType,
    planName: data?.plan_name,
    customerID: commonEventData.customerID,
    orderChannel: commonEventData.orderChannel,
    flowChannel: commonEventData.flowChannel,
    numberType: data?.formValues?.number_type,
    existingPlanTypeSelected: commonEventData.existingPlanTypeSelected,
    portinDonor: commonEventData.portinDonor,
    numberCategory: commonEventData.numberCategory,
    coupon: commonEventData.coupon,
    couponType: commonEventData.referralCodeType
  };
};

const buildMYINFOTroubleEventDataLayer = (data, event) => {
  const commonEventData = constructCommonEventDataObj(data);

  return {
    event,
    transactionId: commonEventData.transactionId,
    simType: commonEventData.simType,
    planName: data?.plan_name,
    customerID: commonEventData.customerID,
    orderChannel: commonEventData.orderChannel,
    flowChannel: commonEventData.flowChannel,
    numberType: data?.formValues?.number_type || commonEventData.numberType,
    existingPlanTypeSelected: commonEventData.existingPlanTypeSelected,
    portinDonor: commonEventData.portinDonor,
    numberCategory: commonEventData.numberCategory,
    coupon: commonEventData.coupon,
    couponType: commonEventData.referralCodeType
  };
};

const buildVerifyPSimNumberClickEventDataLayer = (data, event) => {
  const commonEventData = constructCommonEventDataObj(data);

  return {
    event,
    transactionId: commonEventData.transactionId,
    simType: commonEventData.simType,
    planName: data?.plan_name,
    customerID: commonEventData.customerID,
    orderChannel: commonEventData.orderChannel,
    flowChannel: commonEventData.flowChannel
  };
};

const buildPSimNumberVerifiedEventDataLayer = (data, event) => {
  const commonEventData = constructCommonEventDataObj(data);

  return {
    event,
    transactionId: commonEventData.transactionId,
    simType: commonEventData.simType,
    planName: data?.plan_name,
    customerID: commonEventData.customerID,
    orderChannel: commonEventData.orderChannel,
    flowChannel: commonEventData.flowChannel
  };
};

const buildVerificationOTPEnteredEventDataLayer = (data, event) => {
  const { formValues } = data || {};
  const { number_type } = formValues || {};
  const commonEventData = constructCommonEventDataObj(data);

  return {
    event,
    transactionId: commonEventData.transactionId,
    simType: commonEventData.simType,
    planName: data?.plan_name,
    customerID: commonEventData.customerID,
    orderChannel: commonEventData.orderChannel,
    flowChannel: commonEventData.flowChannel,
    numberType: number_type || commonEventData.numberType,
    numberCategory: commonEventData.numberCategory,
    portinDonor: commonEventData.portinDonor,
    existingPlanTypeSelected: commonEventData.existingPlanTypeSelected,
    coupon: commonEventData.coupon,
    couponType: commonEventData.referralCodeType
  };
};

const constructItems = data => {
  const { sku, textName, basicPrice } = data?.planService || {};
  const orderItems =
    data?.orderParams?.order_items_attributes ||
    data?.orderParams?.order_items ||
    [];

  const planItem = {
    item_name: textName,
    item_id: sku,
    price: basicPrice,
    item_brand: BRAND_NAME,
    item_category: "PLAN",
    item_variant: "PLAN",
    quantity: "1"
  };

  const items = !isEmpty(orderItems)
    ? orderItems.map(item => ({
        item_name: item?.title || item?.name || "",
        item_id: item?.sku || "",
        price: item?.price || 0,
        item_brand: BRAND_NAME,
        item_category: item?.type || "ADDON",
        item_variant: item?.type || "ADDON",
        quantity: "1"
      }))
    : [];

  items.unshift(planItem);

  return items;
};

const constructEnhancedConversionData = (user, address) => {
  if (isEmpty(user) && isEmpty(address)) return {};
  const { email, phone_number, first_name, last_name } = user || {};
  const { zip_code, country, city, hse_blk_tower, street_building_name } =
    address || {};
  const street = compactJoin([hse_blk_tower, street_building_name], " ");
  const enhanced_conversion_data = {
    customerBillingEmail: email || "",
    customerBillingPhone: phone_number || "",
    customerBillingFirstName: first_name || "",
    customerBillingLastName: last_name || "",
    customerBillingStreet: street || "",
    customerBillingCity: city || "",
    customerBillingRegion: city || "",
    customerBillingCountry: country || "",
    customerBillingPostalCode: zip_code || ""
  };
  return { enhanced_conversion_data };
};

export default new GoogleTagManagerAnalytics();
